// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { removeNullAndUndefinedProps } from "@transformers/object";
import { parseSchema } from "@utils/schema";
import Ajv from "ajv";

import serviceActionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { HierarchyAssignment } from "@modules/service/types";
import type { WellbeingServiceContent } from "@modules/service/reducer/wellbeing-services/types";
import * as serviceSelectors from "@modules/service/selectors";

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";

import Step1MainDetails from "./main-details";
import Step2HierarchyScope from "./hierarchy-scope";

type PropsFromState = $Exact<{
  draft: ?WellbeingServiceContent,
  assignments: Array<HierarchyAssignment>,
  schema: ?Object,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  setShowAddModal: typeof serviceActionCreators.SetShowAddWellbeingServiceModal,
  setSchema: typeof serviceActionCreators.SetWellbeingServiceSchema,
  addNew: typeof serviceActionCreators.AddWellbeingService,
  setWellbeingServiceDraft: typeof serviceActionCreators.SetWellbeingServiceDraft,
}>;

type OwnProps = $Exact<{
  clientId: number,
  defaultStep: number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AddWellbeingServicesModal extends Component<Props> {
  validate: *;

  componentDidMount() {
    this.loadSchema();

    this.props.getHierarchyTree({
      clientId: this.props.clientId,
    });

    if (this.props.schema) {
      this.validate = new Ajv().compile(this.props.schema);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validate = new Ajv().compile(this.props.schema);
    }
  }

  loadSchema = async () => {
    const schema = await parseSchema("WellbeingServicesDTO", {
      clientId: this.props.clientId,
    });

    this.props.setSchema(schema);
  };

  handleCancel = () => {
    this.props.setShowAddModal({ show: false });
    this.props.setWellbeingServiceDraft(null);
  };

  handleSubmit = () => {
    if (this.props.draft) {
      this.props.addNew({
        clientId: this.props.clientId,
        draft: this.props.draft,
        assignments: this.props.assignments,
      });
    }
  };

  get isValid() {
    return this.validateDraft && this.props.isAssignmentsValid;
  }

  get validateDraft() {
    if (!this.validate) {
      return false;
    }
    return this.validate(removeNullAndUndefinedProps(this.props.draft || {}));
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("ADD_SERVICE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <LinearWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step
              component={Step1MainDetails}
              title={this.props.t("ADD_WELLBEING_SERVICE_MODAL_STEP_1_TITLE")}
            />
            <Step
              component={Step2HierarchyScope}
              title={this.props.t("ADD_WELLBEING_SERVICE_MODAL_STEP_2_TITLE")}
            />
          </LinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button onClick={this.handleSubmit} disabled={!this.isValid} materialIcon="save">
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    draft: serviceSelectors.getWellbeingServicesDraft(state),
    assignments: serviceSelectors.getWellbeingServiceAssignments(state),
    schema: serviceSelectors.getWellbeingServiceSchema(state),
    isAssignmentsValid: serviceSelectors.isWellbeingServiceAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  setShowAddModal: serviceActionCreators.SetShowAddWellbeingServiceModal,
  setSchema: serviceActionCreators.SetWellbeingServiceSchema,
  addNew: serviceActionCreators.AddWellbeingService,
  setWellbeingServiceDraft: serviceActionCreators.SetWellbeingServiceDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AddWellbeingServicesModal);
